/**
 * Collection Service - Full CRUD for collection entities
 *
 * Supports:
 * - Full CRUD operations (create, read, update, delete)
 * - Soft delete (optional)
 * - Timestamps (created_at, updated_at)
 * - Audit fields (created_by, updated_by)
 * - Pagination, sorting, search, filters
 */

import type {
  CollectionEntityDefinition,
  PaginatedResult,
  ModuleContext,
  EntityServiceHooks,
  SeedConfig
} from '@gzl10/nexus-sdk'
import { resolveLocalized } from '@gzl10/nexus-sdk'
import type { WorkflowTransition } from '@gzl10/nexus-sdk'
import type { EntityQuery } from '@kernel/runtime/index.js'
import { validateWorkflowTransition } from '@kernel/runtime/workflow/validate-transition.js'
import { BaseEntityService } from './base.service.js'
import type { EntityRepository, ICollectionRepository } from '../repositories/types.js'
import { formatTimestamp } from '@kernel/db/schema-helpers.js'
import { loadSeedData, resolveEnvMapping, getEnvDefaultsLockedFields, isEnvDefaultsRecord, resolveEnvDefaults } from '../helpers/index.js'
import { stripEnvLockedFields } from '../helpers/env-mapping.js'

export class CollectionService<T extends Record<string, unknown> = Record<string, unknown>>
  extends BaseEntityService<T> {

  declare readonly definition: CollectionEntityDefinition

  constructor(ctx: ModuleContext, definition: CollectionEntityDefinition, hooks?: EntityServiceHooks<T>, repo?: EntityRepository) {
    super(ctx, definition, hooks, repo)
  }

  /**
   * Get all entities with pagination
   */
  async findAll(query?: EntityQuery): Promise<PaginatedResult<T>> {
    return this.baseFindAll(query)
  }

  /**
   * Apply soft delete filter to all CRUD operations.
   * Migrated from applyTypeFilters(qb) to getTypeFilters() — repo handles WHERE deleted_at IS NULL via filter helper.
   */
  protected override getTypeFilters(): Record<string, unknown> {
    if (this.definition.softDelete) {
      return { deleted_at: null }
    }
    return {}
  }

  /**
   * Post-process items: apply envDefaults overrides to matched records.
   */
  protected override async afterFindAll(items: T[]): Promise<T[]> {
    const envDefaults = this.definition.envDefaults
    if (!envDefaults) return items
    const locked = getEnvDefaultsLockedFields(envDefaults)
    if (locked.length === 0) return items
    return items.map(item => {
      if (!isEnvDefaultsRecord(item as Record<string, unknown>, envDefaults)) return item
      const overridden = resolveEnvMapping(item as Record<string, unknown>, envDefaults.fields)
      return { ...overridden, _envLocked: locked } as unknown as T
    })
  }

  /**
   * Post-process single record after parse: apply envDefaults overrides.
   * Runs before the external afterFindById hook (via processAfterFindById in base).
   */
  protected override async processAfterFindById(record: T | null): Promise<T | null> {
    if (!record) return record

    const envDefaults = this.definition.envDefaults
    let withEnv = record
    if (envDefaults) {
      const locked = getEnvDefaultsLockedFields(envDefaults)
      if (locked.length > 0 && isEnvDefaultsRecord(record as Record<string, unknown>, envDefaults)) {
        const overridden = resolveEnvMapping(record as Record<string, unknown>, envDefaults.fields)
        withEnv = { ...overridden, _envLocked: locked } as unknown as T
      }
    }

    return super.processAfterFindById(withEnv)
  }

  /**
   * Get entity by ID — delegates to baseFindById (repo-backed; soft-delete via getTypeFilters).
   */
  async findById(id: string): Promise<T | null> {
    return this.baseFindById(id)
  }

  /**
   * Create new entity
   */
  async create(data: Partial<T>): Promise<T> {
    // Resolve ID based on entity's idType configuration
    const dataRecord = data as Record<string, unknown>
    const resolvedId = await this.resolveEntityId(dataRecord['id'])
    const entityData: Record<string, unknown> = { ...data }
    if (resolvedId !== undefined) {
      entityData['id'] = resolvedId
    }

    // Add timestamps
    if (this.definition.timestamps) {
      const now = this.getNowTimestamp()
      entityData['created_at'] = now
      entityData['updated_at'] = now
    }

    // Auto-fill audit fields from _authUserId (injected by controller)
    if (this.definition.audit) {
      const authUserId = entityData['_authUserId'] as string | undefined
      if (authUserId) {
        entityData['created_by'] = authUserId
        entityData['updated_by'] = authUserId
      }
    }
    // Force initial workflow state (ignore client-supplied value)
    const wfCreate = this.definition.workflow
    if (wfCreate) {
      const field = wfCreate.field ?? 'status'
      entityData[field] = wfCreate.initial
    }

    // Remove internal field before DB insert
    delete entityData['_authUserId']

    // Run beforeCreate hook
    const processedData = await this.beforeCreate(entityData as Partial<T>)

    const dbData = this.serializeForDb(processedData as Record<string, unknown>)

    // Delegate insert to the repository
    const repo = this.repo as ICollectionRepository<T>
    const created = await repo.create(dbData as Partial<T>)
    const entityId = String((created as Record<string, unknown>)['id'] ?? (dbData as Record<string, unknown>)['id'])

    // Fetch created entity
    const entity = await this.findById(entityId)
    if (!entity) {
      throw new this.errors.AppError(`${resolveLocalized(this.definition.label, 'en')} not found after create`, 404)
    }

    // Run afterCreate hook
    await this.afterCreate(entity)

    // Run retention cleanup asynchronously (absorbed from EventService)
    if (this.definition.retention) {
      this.runRetentionCleanup().catch((err: unknown) => {
        const error = err instanceof Error ? err : new Error('Retention cleanup failed')
        this.logger.error({ err }, 'Retention cleanup failed')
        this.loggerService?.captureException(error, { table: this.table, type: 'collection', action: 'retention' })
      })
    }

    return entity
  }

  /**
   * Update entity
   */
  async update(id: string, data: Partial<T>): Promise<T> {
    // Immutable entities cannot be updated (absorbed from EventService)
    if (this.definition.immutable) {
      throw new this.errors.ForbiddenError('Immutable entities cannot be updated')
    }

    // Check entity exists
    const existing = await this.findById(id)
    if (!existing) {
      throw new this.errors.NotFoundError(resolveLocalized(this.definition.label, 'en'))
    }

    // Validate workflow transition (before any data processing)
    const wf = this.definition.workflow
    let workflowTransition: WorkflowTransition | undefined
    if (wf) {
      const field = wf.field ?? 'status'
      const newValue = (data as Record<string, unknown>)[field] as string | undefined
      const currentValue = (existing as Record<string, unknown>)[field] as string
      try {
        workflowTransition = validateWorkflowTransition(wf, currentValue, newValue)
      } catch (err) {
        throw new this.errors.ValidationError((err as Error).message)
      }
    }

    // Strip env-locked fields if this is the envDefaults record
    const envDefaults = this.definition.envDefaults
    const isDefaultRecord = envDefaults && isEnvDefaultsRecord(existing as Record<string, unknown>, envDefaults)
    const safeData = (isDefaultRecord && envDefaults)
      ? stripEnvLockedFields(data as Record<string, unknown>, envDefaults.fields) as Partial<T>
      : data

    const updateData = { ...safeData } as Record<string, unknown>

    // Update timestamp
    if (this.definition.timestamps) {
      updateData['updated_at'] = this.getNowTimestamp()
    }

    // Auto-fill audit fields
    if (this.definition.audit) {
      const authUserId = updateData['_authUserId'] as string | undefined
      if (authUserId) {
        updateData['updated_by'] = authUserId
      }
    }
    // Remove internal field before DB update
    delete updateData['_authUserId']

    // Run beforeUpdate hook
    const processedData = await this.beforeUpdate(id, updateData as Partial<T>)

    const dbData = this.serializeForDb(processedData as Record<string, unknown>)

    // Delegate update to the repository
    const repo = this.repo as ICollectionRepository<T>
    await repo.update(id, dbData as Partial<T>)

    // Fetch updated entity
    const entity = await this.findById(id)
    if (!entity) {
      throw new this.errors.AppError(`${resolveLocalized(this.definition.label, 'en')} not found after update`, 404)
    }

    // Run afterUpdate hook
    await this.afterUpdate(entity)

    // Emit workflow transition event
    if (wf && workflowTransition) {
      const field = wf.field ?? 'status'
      this.ctx.events.notify('workflow.transition', {
        module: this.moduleName,
        entity: this.entityKey,
        id: (entity as Record<string, unknown>)['id'],
        from: (existing as Record<string, unknown>)[field],
        to: (entity as Record<string, unknown>)[field],
        userId: (data as Record<string, unknown>)['_authUserId'],
        timestamp: new Date().toISOString(),
      })
    }

    return entity
  }

  /**
   * Delete entity (soft delete if enabled)
   */
  async delete(id: string, options?: { actorId?: string }): Promise<void> {
    // Immutable entities cannot be deleted (absorbed from EventService)
    if (this.definition.immutable) {
      throw new this.errors.ForbiddenError('Immutable entities cannot be deleted. Use retention policy.')
    }

    // Check entity exists
    const existing = await this.findById(id)
    if (!existing) {
      throw new this.errors.NotFoundError(resolveLocalized(this.definition.label, 'en'))
    }

    // Prevent deleting the envDefaults record when env vars are defined
    const envDefaultsDef = this.definition.envDefaults
    if (envDefaultsDef && isEnvDefaultsRecord(existing as Record<string, unknown>, envDefaultsDef)) {
      const locked = getEnvDefaultsLockedFields(envDefaultsDef)
      if (locked.length > 0) {
        throw new this.errors.ForbiddenError('Cannot delete the default record while environment variables are set')
      }
    }

    // Run beforeDelete hook
    await this.beforeDelete(id)

    // Store actorId for audit event in afterDelete
    this._deleteActorId = options?.actorId

    if (this.definition.softDelete) {
      // Soft delete — direct UPDATE on the deleted_at column.
      // LIMITATION: ICollectionRepository has no soft-delete primitive yet; refactor pending.
      await this.db(this.table).where('id', id).update({
        deleted_at: this.getNowTimestamp()
      })
    } else {
      // Hard delete via repo
      const repo = this.repo as import('../repositories/types.js').ICollectionRepository
      await repo.delete(id)
    }

    // Run afterDelete hook
    await this.afterDelete(id)
    this._deleteActorId = undefined
  }

  /**
   * Restore soft-deleted entity.
   */
  async restore(id: string): Promise<T> {
    if (!this.definition.softDelete) {
      throw new this.errors.AppError('Soft delete not enabled for this entity', 400)
    }

    // LIMITATION: ICollectionRepository has no includeDeleted flag; restore needs raw access to soft-deleted rows.
    const entity = await this.db(this.table).where('id', id).first<T>()
    if (!entity) {
      throw new this.errors.NotFoundError(resolveLocalized(this.definition.label, 'en'))
    }

    // LIMITATION: ICollectionRepository has no restore primitive (soft-delete is not a standard CRUD op).
    await this.db(this.table).where('id', id).update({
      deleted_at: null,
      updated_at: this.getNowTimestamp()
    })

    const restored = await this.findById(id)
    if (!restored) {
      throw new this.errors.AppError(`${resolveLocalized(this.definition.label, 'en')} not found after restore`, 404)
    }

    return restored
  }

  /**
   * Find multiple entities by IDs
   */
  async findByIds(ids: string[]): Promise<T[]> {
    if (ids.length === 0) return []

    // LIMITATION: ICollectionRepository has no bulkFindByIds method; raw query needed for multi-ID fetch.
    let qb = this.db(this.table).whereIn('id', ids)

    if (this.definition.softDelete) {
      qb = qb.whereNull('deleted_at')
    }

    const rawItems = await qb as T[]
    return this.parseJsonFieldsFromArray(rawItems as Record<string, unknown>[]) as T[]
  }

  // count() inherited from BaseEntityService — getTypeFilters() injects deleted_at: null when softDelete is enabled.

  /**
   * Check if entity exists
   */
  async exists(id: string): Promise<boolean> {
    const entity = await this.findById(id)
    return entity !== null
  }

  /**
   * Seed initial data from definition.seed.
   * Supports inline arrays and SeedConfig objects.
   * Idempotent: only inserts records whose ID doesn't exist yet.
   */
  async seed(): Promise<number> {
    let seeded = 0

    // Auto-create envDefaults record if env vars are defined and record doesn't exist yet
    const envDefaults = this.definition.envDefaults
    if (envDefaults) {
      const defaults = resolveEnvDefaults(envDefaults)
      if (defaults) {
        // LIMITATION: envDefaults match query uses multi-column WHERE not expressible via ICollectionRepository.findAll filters reliably.
        let matchQuery = this.db(this.table)
        for (const [col, val] of Object.entries(envDefaults.match)) {
          matchQuery = matchQuery.where(col, val)
        }
        const existing = await matchQuery.first()
        if (!existing) {
          const data = { ...defaults } as Record<string, unknown>
          data['id'] = this.generateId()
          if (this.definition.timestamps) {
            const now = this.getNowTimestamp()
            data['created_at'] = now
            data['updated_at'] = now
          }
          const dbData = this.serializeForDb(data)
          // LIMITATION: seed insert bypasses hooks intentionally (idempotent one-shot seeding, no business logic needed).
          await this.db(this.table).insert(dbData)
          seeded++
          this.logger.info({ table: this.table, match: envDefaults.match }, 'envDefaults record auto-created')
        }
      }
    }

    const seedDef = this.definition as CollectionEntityDefinition & { seed?: Array<Record<string, unknown>> | SeedConfig }
    const seedData = await loadSeedData(seedDef.seed, this.logger as unknown as import('pino').Logger, 'collection')
    if (seedData.length === 0) {
      if (seeded > 0) this.logger.info({ table: this.table, seeded }, 'Seed data applied')
      return seeded
    }

    for (const item of seedData) {
      const id = item['id'] as string | undefined
      if (!id) continue

      // LIMITATION: seed existence check and insert bypass hooks intentionally (idempotent one-shot seeding).
      const exists = await this.db(this.table).where('id', id).first()
      if (!exists) {
        const data = { ...item, id } as Record<string, unknown>

        if (this.definition.timestamps) {
          const now = this.getNowTimestamp()
          data['created_at'] = now
          data['updated_at'] = now
        }

        const dbData = this.serializeForDb(data)
        await this.db(this.table).insert(dbData)
        seeded++
      }
    }

    const log = seeded > 0 ? this.logger.info.bind(this.logger) : this.logger.debug.bind(this.logger)
    log({ table: this.table, seeded }, 'Seed data applied')
    return seeded
  }

  /**
   * Run retention cleanup based on definition (absorbed from EventService).
   * Deletes old records by age and/or caps total rows.
   */
  async runRetentionCleanup(): Promise<number> {
    const retention = this.definition.retention
    if (!retention) return 0

    let deleted = 0

    // Delete by age (seconds for testing, days for production)
    if (retention.seconds || retention.days) {
      const cutoff = new Date()
      if (retention.seconds) {
        cutoff.setSeconds(cutoff.getSeconds() - retention.seconds)
      } else if (retention.days) {
        cutoff.setDate(cutoff.getDate() - retention.days)
      }

      // LIMITATION: retention cleanup requires complex time-based WHERE + DELETE not supported by ICollectionRepository.
      const result = await this.db(this.table)
        .where('created_at', '<', formatTimestamp(this.db, cutoff))
        .delete()

      deleted += result
    }

    // Delete by max rows (keep newest)
    if (retention.maxRows) {
      // LIMITATION: retention maxRows requires count + subquery DELETE not expressible via ICollectionRepository.
      const countResult = await this.db(this.table)
        .count('* as count')
        .first<{ count: string | number }>()

      const total = Number(countResult?.count ?? 0)
      const toDelete = total - retention.maxRows

      if (toDelete > 0) {
        const oldestIds = await this.db(this.table)
          .select('id')
          .orderBy('created_at', 'asc')
          .limit(toDelete)

        const ids = oldestIds.map((r: Record<string, unknown>) => r['id'] as string)
        const result = await this.db(this.table)
          .whereIn('id', ids)
          .delete()

        deleted += result
      }
    }

    if (deleted > 0) {
      this.logger.info({ table: this.table, deleted }, 'Retention cleanup completed')
    }

    return deleted
  }
}
